import { apiClient } from '@/api/client'
import type {
  CreateTagRequest,
  DeleteEpisodeResponse,
  DeleteProductResponse,
  Episode,
  GetProductsQuery,
  GetProductsResponse,
  GetQueueTracksParams,
  GetTagsResponse,
  MusicAlbumDetailResponse,
  MusicAlbumsResponse,
  MusicArtistsResponse,
  MusicPlaylistDetailResponse,
  MusicPlaylistItem,
  MusicPlaylistsResponse,
  Product,
  QueueTracksResponse,
  Tag,
  UpdateProductTagsRequest,
  UpdateProductTitleResponse,
} from '@/api/type'

export const getProducts = async (params?: GetProductsQuery, signal?: AbortSignal): Promise<GetProductsResponse> => {
  // domain=all の場合は domain パラメータを送らない
  const filteredParams = params ? { ...params } : undefined
  if (filteredParams?.domain === 'all') {
    delete filteredParams.domain
  }
  const res = await apiClient.get(`/p`, { params: filteredParams, signal })
  return res.data
}

export const getProductById = async (productId: number): Promise<Product> => {
  const res = await apiClient.get(`/p/${productId}`)
  return res.data
}

/**
 * episode_id (DB PK) を使ってエピソードを取得
 */
export const getEpisodeById = async (episodeId: number, options?: { includeAdjacent?: boolean }): Promise<Episode> => {
  const res = await apiClient.get(`/e/${episodeId}`, {
    params: options?.includeAdjacent ? { include_adjacent: true } : undefined,
  })
  return res.data
}

export const deleteProduct = async (productId: number): Promise<DeleteProductResponse> => {
  const res = await apiClient.delete(`/p/${productId}`)
  return res.data
}

/**
 * episode_id (DB PK) を指定してエピソードを1話だけ削除（管理者のみ）
 */
export const deleteEpisode = async (episodeId: number): Promise<DeleteEpisodeResponse> => {
  const res = await apiClient.delete(`/e/${episodeId}`)
  return res.data
}

/**
 * エピソードをお気に入りに追加
 */
export const addEpisodeFavorite = async (episodeId: number): Promise<{ is_favorite: boolean }> => {
  const res = await apiClient.post(`/favorites/episodes/${episodeId}`)
  return res.data
}

/**
 * エピソードをお気に入りから削除
 */
export const removeEpisodeFavorite = async (episodeId: number): Promise<{ is_favorite: boolean }> => {
  const res = await apiClient.delete(`/favorites/episodes/${episodeId}`)
  return res.data
}

/**
 * Product サムネイルをアップロード画像で差し替える（管理者のみ）
 */
export const uploadProductThumbnail = async (productId: number, file: File): Promise<void> => {
  const formData = new FormData()
  formData.append('file', file)
  await apiClient.post(`/p/${productId}/thumbnail`, formData)
}

/**
 * Product のタイトルを変更する（管理者のみ）
 */
export const updateProductTitle = async (productId: number, title: string): Promise<UpdateProductTitleResponse> => {
  const res = await apiClient.patch(`/p/${productId}`, { title })
  return res.data
}

// ============================================================
// ショートカット
// ============================================================

export type ShortcutItem = {
  product_id: number
  domain: string
  domain_product_id: string
  title: string
  position: number
}

export const getShortcuts = async (): Promise<ShortcutItem[]> => {
  const res = await apiClient.get('/shortcuts')
  return res.data
}

export const addShortcut = async (productId: number): Promise<{ is_shortcut: boolean }> => {
  const res = await apiClient.post(`/shortcuts/${productId}`)
  return res.data
}

export const removeShortcut = async (productId: number): Promise<{ is_shortcut: boolean }> => {
  const res = await apiClient.delete(`/shortcuts/${productId}`)
  return res.data
}

export const getTags = async (domain: string): Promise<GetTagsResponse> => {
  const res = await apiClient.get(`/tags/${domain}`)
  return res.data
}

/**
 * 新しいタグを作成（管理者のみ）
 */
export const createTag = async (request: CreateTagRequest): Promise<Tag> => {
  const res = await apiClient.post('/tags', request)
  return res.data
}

/**
 * プロダクトのタグを更新（管理者のみ）
 */
export const updateProductTags = async (
  productId: number,
  request: UpdateProductTagsRequest,
): Promise<GetTagsResponse> => {
  const res = await apiClient.patch(`/p/${productId}/tags`, request)
  return res.data
}

export const getQueueTracks = async (params: GetQueueTracksParams): Promise<QueueTracksResponse> => {
  const res = await apiClient.get('/music/queue-tracks', { params })
  return res.data
}

export const getMusicArtists = async (): Promise<MusicArtistsResponse> => {
  const res = await apiClient.get('/music/artists')
  return res.data
}

export const getMusicAlbums = async (tagId?: number): Promise<MusicAlbumsResponse> => {
  const res = await apiClient.get('/music/albums', { params: tagId === undefined ? undefined : { tag_id: tagId } })
  return res.data
}

export const getMusicAlbumDetail = async (productId: number): Promise<MusicAlbumDetailResponse> => {
  const res = await apiClient.get(`/music/albums/${productId}`)
  return res.data
}

export const getMusicPlaylists = async (): Promise<MusicPlaylistsResponse> => {
  const res = await apiClient.get('/music/playlists')
  return res.data
}

export const getMusicPlaylistDetail = async (playlistId: number): Promise<MusicPlaylistDetailResponse> => {
  const res = await apiClient.get(`/music/playlists/${playlistId}`)
  return res.data
}

export const createMusicPlaylist = async (name: string, episodeIds: number[] = []): Promise<MusicPlaylistItem> => {
  const res = await apiClient.post('/music/playlists', { name, episode_ids: episodeIds })
  return res.data
}

export const renameMusicPlaylist = async (playlistId: number, name: string): Promise<MusicPlaylistItem> => {
  const res = await apiClient.patch(`/music/playlists/${playlistId}`, { name })
  return res.data
}

export const deleteMusicPlaylist = async (playlistId: number): Promise<void> => {
  await apiClient.delete(`/music/playlists/${playlistId}`)
}

export const addMusicPlaylistTracks = async (playlistId: number, episodeIds: number[]): Promise<MusicPlaylistItem> => {
  const res = await apiClient.post(`/music/playlists/${playlistId}/tracks`, { episode_ids: episodeIds })
  return res.data
}

export const removeMusicPlaylistTrack = async (playlistId: number, episodeId: number): Promise<void> => {
  await apiClient.delete(`/music/playlists/${playlistId}/tracks/${episodeId}`)
}
